import {
  CancelExchange,
  ExchangeOptions,
  ExchangeReply,
  ExchangeRequest,
  PollOptions,
} from './shared.types';

type ElectronWindow = typeof window & {
  process: {type: string} | undefined;
  api:
    | {
        receive: (evt: string, handler: () => void) => void;
      }
    | undefined;
};

/** Check if the current browser this application is running in is electron
 * copied and modified from https://raw.githubusercontent.com/cheton/is-electron/master/index.js
 * https://github.com/electron/electron/issues/2288
 */
export const isElectron = (): boolean => {
  if (
    (typeof process !== 'undefined' &&
      typeof process.versions === 'object' &&
      Boolean(process.versions.electron)) ||
    (typeof window !== 'undefined' &&
      typeof window.process === 'object' &&
      (window as ElectronWindow).process.type === 'renderer') ||
    (typeof navigator.userAgent === 'string' &&
      navigator.userAgent.indexOf('Electron') >= 0)
  ) {
    return true;
  }
  return false;
};

/**
 * Sends a message to the electron backend without repeating the "window.postMessage" stuff every time.
 */
export const sendElectronMessage = (message: ExchangeRequest): void => {
  if (isElectron()) {
    window.postMessage(message, window.location.origin);
  }
};

const DEFAULT_EXCHANGE_TIMEOUT_IN_MS = 30_000;

type PendingExchange = {
  requestId: string;
  type: string;
  // we have to allow all kinds of payloads here as long as the typesafety is guaranteed on the outside we should be safe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responsePayloadHandler: (payload: any) => void;
  timeout: ReturnType<typeof setTimeout>;
};

const pendingExchanges = new Map<string, PendingExchange>();

/**
 * Sends a message with the specified messageType to the lexian-browser and handles the response's
 * payload with the specified responsePayloadHandler.
 * If the type is not included in the response the event is _not_ handled.
 * Returns a handle which cancels the exchange when it is no longer of interest.
 *
 * Every message carries a request id which the lexian-browser echoes back, so
 * that a reply can be attributed to the request that asked for it. A request
 * which stays unanswered for longer than its timeout is dropped and its
 * onTimeout is called.
 */
export function exchangeElectronMessage<T extends ExchangeRequest, U>(
  message: T,
  responsePayloadHandler: (response: U) => void,
  {
    timeoutInMs = DEFAULT_EXCHANGE_TIMEOUT_IN_MS,
    onTimeout,
  }: ExchangeOptions = {}
): CancelExchange {
  if (!isElectron()) {
    return () => undefined;
  }

  const requestId = crypto.randomUUID();
  const timeout = setTimeout(() => {
    cancelExchange(requestId);
    // eslint-disable-next-line no-console
    const log = onTimeout ? console.warn : console.error;
    log(
      `Electron exchange "${message.type}" (${requestId}) timed out after ${timeoutInMs}ms`
    );
    onTimeout?.();
  }, timeoutInMs);

  pendingExchanges.set(requestId, {
    requestId,
    type: message.type,
    responsePayloadHandler,
    timeout,
  });
  sendElectronMessage({...message, requestId});

  return () => cancelExchange(requestId);
}

/**
 * Polls the lexian-browser with the given message: a round starts immediately
 * and then every intervalInMs. The round still pending when the next one
 * starts is cancelled, so rounds can never pile up. Returns a handle which
 * stops polling and cancels the pending round.
 */
export function pollExchangeElectronMessage<T extends ExchangeRequest, U>(
  message: T,
  responsePayloadHandler: (response: U) => void,
  {intervalInMs, ...exchangeOptions}: PollOptions
): CancelExchange {
  if (!isElectron()) {
    return () => undefined;
  }

  let cancelRound: CancelExchange | undefined;
  const startRound = (): void => {
    cancelRound?.();
    cancelRound = exchangeElectronMessage(message, responsePayloadHandler, {
      timeoutInMs: intervalInMs,
      ...exchangeOptions,
    });
  };

  startRound();
  const interval = setInterval(startRound, intervalInMs);

  return () => {
    clearInterval(interval);
    cancelRound?.();
  };
}

/**
 * Cancel the exchange with the given request id.
 */
function cancelExchange(requestId: string): void {
  const pendingExchange = pendingExchanges.get(requestId);

  if (!pendingExchange) {
    return;
  }

  clearTimeout(pendingExchange.timeout);
  pendingExchanges.delete(requestId);
}

function mainElectronMessageHandler(
  event: MessageEvent<{type: string; payload: ExchangeReply}>
): void {
  if (!isElectron()) {
    return;
  }

  // The event's data type needs to be renderer, otherwise we could
  // unintentionally remove the eventListener by registering any random message
  // posted to the window.
  if (event.data.type !== 'renderer') {
    return;
  }

  const {type, requestId, payload} = event.data.payload;

  const pendingExchange = findPendingExchange(type, requestId);

  if (!pendingExchange) {
    return;
  }

  cancelExchange(pendingExchange.requestId);

  pendingExchange.responsePayloadHandler(payload);
}

/**
 * A lexian-browser released before the request id was introduced echoes no
 * request id back, so such a reply is attributed to the request of the same
 * type which has been waiting the longest.
 */
function findPendingExchange(
  type: string,
  requestId: string | undefined
): PendingExchange | undefined {
  if (requestId) {
    return pendingExchanges.get(requestId);
  }

  return [...pendingExchanges.values()].find(
    (pendingExchange) => pendingExchange.type === type
  );
}

window.addEventListener('message', mainElectronMessageHandler);
